/**
 * ChatBubble, renders one conversation turn (user OR assistant).
 *
 * For assistant turns, additionally renders inline:
 *   - 3-card recommendation carousel (when cards present)
 *   - "See all {N}" affordance after the carousel
 *
 * Card metadata is dynamic per the LLM's chosen highlight_fields
 * (Lucky 2026-05-13: a spa voucher doesn't need "Lead time"; a
 * cake doesn't need "Validity").
 *
 * Sprint 1.7o, when `animate` is true, assistant text streams in
 * via a typewriter effect. Cards + the "See all" affordance hold
 * until the text is fully revealed so the carousel doesn't pre-empt
 * the conversational beat. Cheap perceived-streaming win without
 * the lift of real Anthropic SSE through Supabase Edge Functions.
 */

import { useEffect, useRef, useState } from "react";
import { SkuCard, SkuCardData } from "./SkuCard";
import { ChatMarkdown } from "./ChatMarkdown";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

// Tuned by feel: ~28 chars/sec reads as "Lembra typing thoughtfully"
// rather than "Lembra spamming the keyboard." Faster looks fake;
// slower drags. Skip animation for short messages (≤40 chars) where
// the typewriter is more annoying than helpful.
const CHARS_PER_SEC = 28;
const MIN_TEXT_LENGTH_FOR_ANIMATION = 40;

interface ChatBubbleProps {
  role: "user" | "assistant";
  text: string;
  cards?: SkuCardData[];
  /** Total candidate count if more options exist beyond the carousel. */
  totalCandidates?: number;
  /** MS61a: was `onSend`. Fires when the buyer clicks a card's
   *  "View →" CTA. Parent opens the ProductDetailModal; the actual
   *  send-to-recipient CTA lives inside that modal now. */
  onView?: (sku: SkuCardData) => void;
  onSeeAll?: () => void;
  /** Sprint 1.7o, when true, animate assistant text in.
   *  Skipped on session restore (history that was already revealed). */
  animate?: boolean;
  /** MS598: this assistant bubble is STREAMING in live (the flag-gated
   *  incremental-render path). The `text` prop grows on every render as
   *  server `delta`s accumulate, so:
   *    - render it as PLAIN TEXT (no markdown) — a still-growing string
   *      would otherwise flicker between half-parsed and parsed states as
   *      `**` / `_` / list markers open and close mid-stream. The
   *      finalized turn (a normal ChatBubble, streaming=false) renders the
   *      full markdown once the stream lands.
   *    - skip the Sprint-1.7o typewriter entirely — the text is ALREADY
   *      arriving incrementally from the server; layering the per-char
   *      reveal on top would double-animate and lag the real stream.
   *    - show a soft caret so the bubble reads as actively writing.
   *  Cards still hold until they're present (they arrive atomically on the
   *  `cards` event) and reveal via the same gentle fade as a normal turn. */
  streaming?: boolean;
  onEdit?: () => void;
}

export function ChatBubble({
  role,
  text,
  cards,
  totalCandidates,
  onView,
  onSeeAll,
  animate = false,
  streaming = false,
  onEdit,
}: ChatBubbleProps) {
  const isUser = role === "user";
  const reducedMotion = useReducedMotion();

  // Sprint 1.7o, typewriter state. Only ever populated for assistant
  // bubbles where animate=true AND the text is long enough to be worth
  // animating. For everything else displayedText stays in lockstep
  // with the prop on every render.
  //
  // MS183: respect prefers-reduced-motion. Same principle as MS177
  // (SmartIntentBar typewriter, useRotatingVerb): JS-driven motion
  // bypasses the global CSS reduced-motion override. When the user
  // opts in at the OS level, render the bubble text immediately
  // and skip the per-character reveal.
  // MS598: a streaming bubble never runs the typewriter — the server's
  // deltas ARE the animation. `streaming` short-circuits shouldAnimate so
  // `displayedText` tracks the live `text` prop on every render (the
  // !shouldAnimate branch in the effect below keeps them in lockstep).
  const shouldAnimate =
    !streaming &&
    animate &&
    !isUser &&
    !reducedMotion &&
    (text?.length ?? 0) > MIN_TEXT_LENGTH_FOR_ANIMATION;

  const [displayedText, setDisplayedText] = useState<string>(shouldAnimate ? "" : text);
  // Track which text we've already animated so a parent re-render
  // (e.g. when cards land) doesn't restart the animation.
  const animatedForRef = useRef<string | null>(shouldAnimate ? null : text);

  useEffect(() => {
    if (!shouldAnimate) {
      setDisplayedText(text);
      animatedForRef.current = text;
      return;
    }
    if (animatedForRef.current === text) return;
    animatedForRef.current = text;
    setDisplayedText("");

    let cancelled = false;
    const totalChars = text.length;
    const intervalMs = Math.max(15, Math.round(1000 / CHARS_PER_SEC));
    let i = 0;
    const tick = () => {
      if (cancelled) return;
      // Reveal in small chunks (3 chars per tick), feels smoother
      // than 1-char ticks at higher speeds, easier on React state churn.
      i = Math.min(totalChars, i + 3);
      setDisplayedText(text.slice(0, i));
      if (i < totalChars) {
        timer = window.setTimeout(tick, intervalMs);
      }
    };
    let timer = window.setTimeout(tick, intervalMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [text, shouldAnimate]);

  // Hold cards/chips until the text is fully revealed, keeps the
  // conversational beat. Once animation completes (or if there's no
  // animation), they appear immediately.
  // MS598: while streaming there's no typewriter to wait on — cards arrive
  // atomically on their own `cards` event AFTER the prose deltas, so the
  // moment they're present they should reveal (the server already paced
  // the beat). `streaming` therefore doesn't gate attachments.
  const textFullyRevealed = displayedText.length === text.length;
  const showAttachments = streaming || !shouldAnimate || textFullyRevealed;

  return (
    <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex w-full max-w-[min(680px,92%)] flex-col ${isUser ? "items-end" : "items-start"} gap-3 min-w-0`}
      >
        {/* Speaker label (assistant only, gives the bubble identity) */}
        {!isUser && (
          <p
            className="ml-1 text-xs uppercase tracking-wider text-charcoal/55"
            style={{ fontFamily: "DM Sans, sans-serif", letterSpacing: "0.08em" }}
          >
            Lembra
          </p>
        )}

        {/* Bubble body */}
        {/*
          MS190: assistant bubbles render markdown once the typewriter
          finishes (so `**Bold**` lands as <strong> instead of literal
          asterisks). During the reveal we keep plain text + the
          typewriter caret — rendering markdown mid-reveal would flicker
          between half-parsed and fully-parsed states as `**` opens and
          closes mid-stream. User bubbles + non-animated assistant
          bubbles also use markdown (zero-cost: text without any
          markdown syntax renders as a single <p>).

          User bubbles still get plain-text treatment — buyers don't
          typically type `**bold**`, and rendering their text as
          markdown would surprise-format anything they paste.
        */}
        {text && (
          <div
            className={
              isUser
                ? "rounded-2xl bg-coral px-4 py-2.5 text-white"
                : "rounded-2xl bg-charcoal/5 px-4 py-3 text-navy"
            }
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {/* MS598: `streaming` joins the plain-text branch for the same
                reason the mid-typewriter reveal uses it — a still-growing
                string must not render through the markdown parser (half-
                parsed `**`/list flicker). The caret shows while either the
                typewriter is mid-reveal OR the bubble is streaming live. */}
            {isUser || streaming || (shouldAnimate && !textFullyRevealed) ? (
              <p className="whitespace-pre-wrap text-base leading-relaxed">
                {displayedText}
                {/* Caret while animating or streaming, disappears once the
                    text is fully revealed / the stream finalizes. */}
                {(streaming || (shouldAnimate && !textFullyRevealed)) && (
                  <span
                    aria-hidden="true"
                    className="inline-block w-[2px] h-[1em] ml-0.5 bg-navy/60 align-middle animate-pulse"
                  />
                )}
              </p>
            ) : (
              <ChatMarkdown content={displayedText} />
            )}
          </div>
        )}

        {isUser && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="mr-1 text-xs text-navy/55 underline-offset-2 hover:text-coral hover:underline focus:outline-none focus:ring-2 focus:ring-coral rounded"
            style={{ fontFamily: "DM Sans, sans-serif" }}
            aria-label="Edit message"
          >
            Edit
          </button>
        )}

        {/* Inline carousel for assistant card recommendations.
            MS196: was `-mx-4 w-screen max-w-[680px] px-4` (edge-to-edge
            breakout on mobile). That worked on /chat where the main is
            edge-to-edge, but in InlineConcierge the thread is inside a
            scroll container with `overflow-y-auto` — per the CSS spec
            this forces `overflow-x: auto` too, clipping the breakout
            horizontally and hiding the cards entirely. Lucky's bug
            ("recommendations don't show in the chat widget on mobile")
            was the same carousel-rendering symptom as MS195 but with a
            different root cause: the cards rendered, they were just
            positioned outside their bubble's clip rect.
            Fit-to-bubble (`w-full`) works in both surfaces; cards
            still scroll horizontally via the inner overflow-x-auto. */}
        {!isUser && showAttachments && cards && cards.length > 0 && onView && (
          <div className="w-full min-w-0">
            <div className="flex gap-3 overflow-x-auto pb-2 pr-1 snap-x snap-mandatory scrollbar-thin">
              {cards.map((c, index) => (
                <SkuCard
                  key={c.sku_id}
                  sku={c}
                  variant="carousel"
                  onView={onView}
                  // MS540: the assistant typically returns ≤3 cards
                  // visible side-by-side in the bubble — all of them
                  // are above the fold the moment the bubble renders.
                  // AUDIT-029 saw all three blank for >15s with the
                  // lazy default. Eager-load every card in the row.
                  priority={index < 3}
                  surface="chat"
                />
              ))}
            </div>

            {typeof totalCandidates === "number" && totalCandidates > cards.length && onSeeAll && (
              <button
                type="button"
                onClick={onSeeAll}
                className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-navy underline-offset-2 hover:text-coral hover:underline focus:outline-none focus:ring-2 focus:ring-coral rounded"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                See all {totalCandidates} options →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
